import { useEffect } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { authTelemetry } from '@/app/services/authTelemetry';

/**
 * Hook to automatically track authentication telemetry and health metrics
 */
export const useAuthTelemetry = () => {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    // Track session start when user becomes authenticated
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      // Only track if this is a new session (not already tracked)
      const sessionTracked = sessionStorage.getItem('auth_session_tracked');
      if (!sessionTracked) {
        authTelemetry.trackSessionStart(account, 'silent');
        sessionStorage.setItem('auth_session_tracked', 'true');
      }
    }

    // Clear session tracking when user logs out
    if (!isAuthenticated) {
      sessionStorage.removeItem('auth_session_tracked');
    }
  }, [isAuthenticated, accounts]);

  useEffect(() => {
    // Track authentication health summary periodically
    const healthInterval = setInterval(() => {
      if (isAuthenticated) {
        authTelemetry.trackAuthHealthSummary();
      }
    }, 15 * 60 * 1000); // Every 15 minutes

    // Track session end on page unload
    const handleBeforeUnload = () => {
      if (isAuthenticated) {
        authTelemetry.trackSessionEnd('page_unload');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(healthInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAuthenticated]);

  // Track user activity for correlation with auth events
  useEffect(() => {
    if (!isAuthenticated) return;

    const trackActivity = (type: 'mouse' | 'keyboard') => {
      authTelemetry.trackUserActivity(type);
    };

    // Throttled activity tracking to avoid spam
    let lastMouseTrack = 0;
    let lastKeyboardTrack = 0;
    const throttleMs = 60000; // 1 minute

    const handleMouseActivity = () => {
      const now = Date.now();
      if (now - lastMouseTrack > throttleMs) {
        trackActivity('mouse');
        lastMouseTrack = now;
      }
    };

    const handleKeyboardActivity = () => {
      const now = Date.now();
      if (now - lastKeyboardTrack > throttleMs) {
        trackActivity('keyboard');
        lastKeyboardTrack = now;
      }
    };

    // Add throttled event listeners
    document.addEventListener('mousemove', handleMouseActivity, { passive: true });
    document.addEventListener('keydown', handleKeyboardActivity, { passive: true });

    return () => {
      document.removeEventListener('mousemove', handleMouseActivity);
      document.removeEventListener('keydown', handleKeyboardActivity);
    };
  }, [isAuthenticated]);
};
